import { divIcon, LatLngTuple } from 'leaflet';
import React, { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import ReactDOMServer from 'react-dom/server';
import { Marker, Popup } from 'react-leaflet';
import { useTranslation } from 'react-i18next';

import { SRBMTrapPopup } from '../components/SBSTrapPopup';
import { MCTTrapPopup } from '../components/MCTTrapPopup';
import { TempMarkerPopup } from '../components/TempMarkerPopup';
import { HubPopup } from '../components/HubPopup';
import { VulnerabilityPopup } from '../components/VulnerabilityPopup';
import { EncounterPopup } from '../components/EncounterPopup';
import { STTrapPopup } from '../components/STTrapPopup';

import MctSvg from './MakerSvgS/MctSvg';
import SbsSvg from './MakerSvgS/SbsSvg';
import HubSvg from './MakerSvgS/HubSvg';
import VulSvg from './MakerSvgS/VulSvg';
import EncSvg from './MakerSvgS/EncSvg';
import SRSTSvg from './MakerSvgS/SRSTSvg';
import SMSTSvg from './MakerSvgS/SMSTSvg';

import usePutTrapNewLocation from 'hooks/InteractiveMap/usePutTrapNewLocation';
import usePutHubNewLocation from 'hooks/InteractiveMap/usePutHubNewLocation';
import usePutVulnerabilityNewLocation from 'hooks/InteractiveMap/usePutVulnerabilityNewLocation';
import usePutEncounterNewLocation from 'hooks/InteractiveMap/usePutEncounterNewLocation';

interface Props {
  device: any;
  position: LatLngTuple;
  deviceType: 'srbm' | 'mct' | 'srst' | 'smst' | 'hub' | 'vul' | 'enc';
  markerRef?: React.Ref<any> | ((el: any) => void);
  isTemp?: boolean;
  plan_id?: number;
  planSlug?: string;
  onTempMarkerSaved?: () => void;
  trapsConnected?: number;
  mctTraps?: number;
  srbmTraps?: number;
  smstTraps?: number;
  srstTraps?: number;
  hubCount?: number;
  objectSlug?: string;
  refetchEncounters?: () => void;
  opacity?: number;
  isActiveFilterArchive?: boolean;
}

const DeviceMarker: React.FC<Props> = ({
  device,
  position,
  deviceType,
  markerRef,
  isTemp,
  plan_id,
  planSlug,
  onTempMarkerSaved,
  trapsConnected,
  mctTraps,
  srbmTraps,
  smstTraps,
  srstTraps,
  hubCount,
  objectSlug,
  refetchEncounters,
  opacity = 1,
  isActiveFilterArchive = false,
}) => {
  const [markerPosition, setMarkerPosition] = useState<LatLngTuple>(position);
  const [showMoveActions, setShowMoveActions] = useState(isTemp || false);
  const internalMarkerRef = useRef<any>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { putTrapNewLocation, isLoading } = usePutTrapNewLocation();
  const { putHubNewLocation, isLoading: isHubLoading } = usePutHubNewLocation();
  const { putVulnerabilityNewLocation, isLoading: isVulLoading } = usePutVulnerabilityNewLocation();
  const { putEncounterNewLocation, isLoading: isEncLoading } = usePutEncounterNewLocation();
  const { t } = useTranslation();

  const wrapWithOpacity = (svgMarkup: string) => {
    return `<div style="opacity: ${opacity}; transition: opacity 0.3s ease;">${svgMarkup}</div>`;
  };

  const srstIcon = divIcon({
    className: '',
    html: wrapWithOpacity(
      ReactDOMServer.renderToStaticMarkup(
        <SRSTSvg
          caughtPests={device.caught_pests || 0}
          status={device.status}
          number={device.number}
          activityLabel={device.activity?.label}
        />,
      ),
    ),
    iconSize: [50, 50],
    iconAnchor: [25, 30],
  });

  const smstIcon = divIcon({
    className: '',
    html: wrapWithOpacity(
      ReactDOMServer.renderToStaticMarkup(
        <SMSTSvg
          caughtPests={device.caught_pests || 0}
          status={device.status}
          number={device.number}
          activityLabel={device.activity?.label}
        />,
      ),
    ),
    iconSize: [30, 30],
    iconAnchor: [25, 30],
  });

  const mctIcon = divIcon({
    className: '',
    html: wrapWithOpacity(
      ReactDOMServer.renderToStaticMarkup(
        <MctSvg
          caughtPests={device.caught_pests || 0}
          status={device.status}
          number={device.number}
          activityLabel={device.activity?.label}
        />,
      ),
    ),
    iconSize: [50, 61],
    iconAnchor: [25, 30],
  });

  const sbsIcon = divIcon({
    className: '',
    html: wrapWithOpacity(
      ReactDOMServer.renderToStaticMarkup(
        <SbsSvg
          caughtPests={device.caught_pests || 0}
          percentageOfFilling={device.percentage_of_filling || 0}
          status={device.status}
          number={device.number}
          activityLabel={device.activity?.label}
        />,
      ),
    ),
    iconSize: [50, 61],
    iconAnchor: [25, 30],
  });

  const hubIcon = divIcon({
    className: '',

    html: wrapWithOpacity(
      ReactDOMServer.renderToStaticMarkup(
        <HubSvg
          // @ts-ignore
          number={device.sequence_number}
          status={device.status}
          // @ts-ignore
          last_connect={device.last_connect}
          is_many={!!hubCount && hubCount > 1}
        />,
      ),
    ),
    iconSize: [30, 60],
    iconAnchor: [15, 30],
  });

  const vulIcon = divIcon({
    className: '',
    html: wrapWithOpacity(
      ReactDOMServer.renderToStaticMarkup(<VulSvg category={device.category} criticality={device.criticality} />),
    ),
    iconSize: [38, 38],
    iconAnchor: [19, 19],
  });

  const encIcon = divIcon({
    className: '',
    html: wrapWithOpacity(ReactDOMServer.renderToStaticMarkup(<EncSvg />)),
    iconSize: [38, 38],
    iconAnchor: [19, 19],
  });

  useEffect(() => {
    if (showMoveActions && internalMarkerRef.current) {
      internalMarkerRef.current.openPopup();
    }
  }, [showMoveActions]);

  const handleDragEnd = (e: any) => {
    setMarkerPosition([e.target.getLatLng().lat, e.target.getLatLng().lng]);
    if (internalMarkerRef.current) {
      internalMarkerRef.current.openPopup();
    }
  };

  const handleStartMove = () => {
    if (isActiveFilterArchive) {
      enqueueSnackbar(t('InteractiveMap.addMenu.alertOpenArhive'), { variant: 'warning' });
      return;
    }
    setShowMoveActions(prev => !prev);
    if (internalMarkerRef.current) {
      internalMarkerRef.current.openPopup();
    }
  };

  const handleMoveAction = (action: 'adjustment' | 'new_place', caught_pests?: number | null) => {
    if (caught_pests && caught_pests > 0) {
      enqueueSnackbar(t('Popups.Snackbars.cantChangeThePos'), { variant: 'error' });
    } else {
      putTrapNewLocation(
        {
          slug: device.slug,
          action,
          plan_id: isTemp ? plan_id : undefined,
          newLocationData: {
            x_coord: Math.floor(markerPosition[0]),
            y_coord: Math.floor(markerPosition[1]),
          },
        },
        {
          onSuccess: () => {
            enqueueSnackbar(t('Popups.Snackbars.successfullyChangedPos'), { variant: 'success' });
            if (isTemp) {
              onTempMarkerSaved?.();
            } else {
              setShowMoveActions(false);
              if (internalMarkerRef.current) {
                internalMarkerRef.current.closePopup();
              }
            }
          },
          onError: () => {
            enqueueSnackbar(t('Popups.Snackbars.failedChangePos'), { variant: 'error' });
          },
        },
      );
    }
  };

  const handleHubMoveAction = (action: 'adjustment' | 'new_place') => {
    putHubNewLocation(
      {
        slug: device.slug,
        action,
        plan_id: isTemp ? plan_id : undefined,
        newLocationData: {
          x_coord: Math.floor(markerPosition[0]),
          y_coord: Math.floor(markerPosition[1]),
        },
      },
      {
        onSuccess: () => {
          enqueueSnackbar(t('Popups.Snackbars.successfullyChangedPos'), { variant: 'success' });
          if (isTemp) {
            onTempMarkerSaved?.();
          } else {
            setShowMoveActions(false);
            if (internalMarkerRef.current) {
              internalMarkerRef.current.closePopup();
            }
          }
        },
        onError: () => {
          enqueueSnackbar(t('Popups.Snackbars.failedChangePos'), { variant: 'error' });
        },
      },
    );
  };

  const handleVulMove = () => {
    putVulnerabilityNewLocation(
      {
        objectSlug: objectSlug || '',
        vulnerabilityId: device.id,
        newLocationData: {
          x_coord: Math.floor(markerPosition[0]),
          y_coord: Math.floor(markerPosition[1]),
        },
      },
      {
        onSuccess: () => {
          enqueueSnackbar(t('Popups.Snackbars.successfullyChangedPos'), { variant: 'success' });
          if (isTemp) {
            onTempMarkerSaved?.();
          }
          setShowMoveActions(false);
          if (internalMarkerRef.current) {
            internalMarkerRef.current.closePopup();
          }
        },
        onError: () => {
          enqueueSnackbar(t('Popups.Snackbars.failedChangePos'), { variant: 'error' });
        },
      },
    );
  };

  const handleEncMove = () => {
    putEncounterNewLocation(
      {
        objectSlug: objectSlug || '',
        encounterId: device.id,
        newLocationData: {
          floor_plan: planSlug || '',
          x_coord: Math.floor(markerPosition[0]),
          y_coord: Math.floor(markerPosition[1]),
        },
      },
      {
        onSuccess: () => {
          enqueueSnackbar(t('Popups.Snackbars.successfullyChangedPos'), { variant: 'success' });
          if (isTemp) {
            onTempMarkerSaved?.();
          }
          setShowMoveActions(false);
          refetchEncounters?.();
          if (internalMarkerRef.current) {
            internalMarkerRef.current.closePopup();
          }
        },
        onError: () => {
          enqueueSnackbar(t('Popups.Snackbars.failedChangePos'), { variant: 'error' });
        },
      },
    );
  };

  // Compose refs: support both internal and external (from parent)
  const handleMarkerRef = (el: any) => {
    internalMarkerRef.current = el;
    if (typeof markerRef === 'function') {
      markerRef(el);
    } else if (markerRef && typeof markerRef === 'object') {
      // @ts-ignore
      markerRef.current = el;
    }
  };

  return (
    <Marker
      position={markerPosition}
      icon={
        deviceType === 'mct'
          ? mctIcon
          : deviceType === 'srbm'
            ? sbsIcon
            : deviceType === 'hub'
              ? hubIcon
              : deviceType === 'srst'
                ? srstIcon
                : deviceType === 'smst'
                  ? smstIcon
                  : deviceType === 'vul'
                    ? vulIcon
                    : encIcon
      }
      draggable={
        isTemp ||
        showMoveActions ||
        (deviceType === 'vul' && showMoveActions) ||
        (deviceType === 'enc' && showMoveActions)
      }
      eventHandlers={{
        dragstart: () => {
          if (internalMarkerRef.current) internalMarkerRef.current.openPopup();
        },
        dragend: handleDragEnd,
        click: () => internalMarkerRef.current && internalMarkerRef.current.openPopup(),
      }}
      ref={handleMarkerRef}
    >
      <Popup offset={[0, -10]}>
        {deviceType === 'enc' ? (
          <EncounterPopup
            encounter={device}
            onMove={handleStartMove}
            onSave={handleEncMove}
            isMoving={showMoveActions}
            isLoading={isEncLoading}
            isActiveFilterArchive={isActiveFilterArchive}
          />
        ) : deviceType === 'vul' ? (
          <VulnerabilityPopup
            vulnerability={device}
            onMove={() => {
              if (isActiveFilterArchive) {
                enqueueSnackbar(t('InteractiveMap.addMenu.alertOpenArhive'), { variant: 'warning' });
                return;
              }
              setShowMoveActions(true);
            }}
            onSave={handleVulMove}
            isMoving={showMoveActions}
            isLoading={isVulLoading}
            isActiveFilterArchive={isActiveFilterArchive}
          />
        ) : isTemp ? (
          <TempMarkerPopup
            trap={device}
            onMoveAction={deviceType === 'hub' ? handleHubMoveAction : handleMoveAction}
            isLoading={deviceType === 'hub' ? isHubLoading : isLoading}
            isHub={deviceType === 'hub'}
          />
        ) : deviceType === 'mct' ? (
          <MCTTrapPopup
            trap={device}
            onMove={handleStartMove}
            showMoveActions={showMoveActions}
            onMoveAction={handleMoveAction}
            isMoving={showMoveActions}
            isLoading={isLoading}
            isActiveFilterArchive={isActiveFilterArchive}
          />
        ) : deviceType === 'srbm' ? (
          <SRBMTrapPopup
            trap={device}
            onMove={handleStartMove}
            showMoveActions={showMoveActions}
            onMoveAction={handleMoveAction}
            isMoving={showMoveActions}
            isLoading={isLoading}
            isActiveFilterArchive={isActiveFilterArchive}
          />
        ) : deviceType === 'srst' || deviceType === 'smst' ? (
          <STTrapPopup
            trap={device}
            onMove={handleStartMove}
            showMoveActions={showMoveActions}
            onMoveAction={handleMoveAction}
            isMoving={showMoveActions}
            isLoading={isLoading}
            trapType={deviceType}
            isActiveFilterArchive={isActiveFilterArchive}
          />
        ) : (
          <HubPopup
            hub={device}
            onMove={handleStartMove}
            showMoveActions={showMoveActions}
            onMoveAction={handleHubMoveAction}
            isMoving={showMoveActions}
            isLoading={isHubLoading}
            trapsConnected={trapsConnected || 0}
            mctTraps={mctTraps || 0}
            srbmTraps={srbmTraps || 0}
            smstTraps={smstTraps || 0}
            srstTraps={srstTraps || 0}
            isActiveFilterArchive={isActiveFilterArchive}
          />
        )}
      </Popup>
    </Marker>
  );
};

export default DeviceMarker;
